import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { joinWaitlist } from '../../services/subscriptionService';
import './PurchaseComingSoonModal.css';

interface PurchaseComingSoonModalProps {
  open: boolean;
  onClose: () => void;
  /** Where the CTA was triggered — used for analytics/segmentation. */
  source?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const PurchaseComingSoonModal: React.FC<PurchaseComingSoonModalProps> = ({ open, onClose, source }) => {
  const { t, i18n } = useTranslation('pricing');
  const { user } = useAuth();
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<'idle' | 'success' | 'already' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEmail(user?.email ?? '');
    setState('idle');
    setErrorMsg(null);
    const timer = window.setTimeout(() => emailInputRef.current?.focus(), 60);
    return () => window.clearTimeout(timer);
  }, [open, user?.email]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      // Trap Tab within the panel so focus cannot escape to elements behind the
      // backdrop (WCAG 2.1 AA — focus order must stay within the modal).
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('aria-hidden'));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const trimmed = email.trim();
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  const canSubmit = isValidEmail && !submitting && state !== 'success' && state !== 'already';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await joinWaitlist(trimmed, i18n.language, source || 'pricing_cta');
      setState(res.alreadyRegistered ? 'already' : 'success');
    } catch (err: any) {
      setState('error');
      setErrorMsg(err?.response?.data?.message || err?.message || t('comingSoon.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const isDone = state === 'success' || state === 'already';

  return (
    <div className="pricing-modal coming-soon-modal" onClick={onClose}>
      <div
        ref={panelRef}
        className="pricing-modal__panel coming-soon-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="coming-soon-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="coming-soon-modal__badge">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>{t('comingSoon.kicker')}</span>
        </div>

        <h3 id="coming-soon-title">{t('comingSoon.title')}</h3>
        <p>{t('comingSoon.description')}</p>

        {!isDone && (
          <form className="coming-soon-modal__form" onSubmit={handleSubmit}>
            <label htmlFor="coming-soon-email" className="coming-soon-modal__label">
              {t('comingSoon.emailLabel')}
            </label>
            <input
              id="coming-soon-email"
              ref={emailInputRef}
              type="email"
              className="coming-soon-modal__input"
              placeholder={t('comingSoon.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
            {state === 'error' && errorMsg && (
              <p className="coming-soon-modal__error" role="alert">{errorMsg}</p>
            )}
            <div className="pricing-modal__actions coming-soon-modal__actions">
              <button type="button" className="pricing-modal__cancel" onClick={onClose}>
                {t('comingSoon.close')}
              </button>
              <button
                type="submit"
                className="pricing-modal__confirm coming-soon-modal__submit"
                disabled={!canSubmit}
              >
                {submitting ? t('comingSoon.submitting') : t('comingSoon.cta')}
              </button>
            </div>
          </form>
        )}

        {isDone && (
          <div className="coming-soon-modal__success">
            <div className="coming-soon-modal__success-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <p className="coming-soon-modal__success-text">
              {state === 'already' ? t('comingSoon.alreadyRegistered') : t('comingSoon.successMessage')}
            </p>
            <div className="pricing-modal__actions coming-soon-modal__actions">
              <button type="button" className="pricing-modal__confirm" onClick={onClose}>
                {t('comingSoon.gotIt')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseComingSoonModal;
